import { isStaffUser } from 'utils/staff';
import React, { useCallback, useContext, useMemo, useState } from "react";

import { VideoListContainer, } from "./styled";
import DashboardHomeCard from "components/Cards/DashboardHomeCard";
import TrailDocumentCard from "components/Cards/TrailDocumentCard";
import VideoCard from "components/Cards/VideoCard";
import HorizontalScrollCarousel from "components/HorizontalScrollCarousel";
import Wrapper from "components/Wrapper";
import ItemsList from "components/Cards/ItemsList";
import CatalogState from "components/CatalogState";
import { buildS3DownloadUrl } from "services/s3Download";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { CreateShareLink } from "services/shareLink";
import { buildPortalDetailRoute, normalizePortal } from "utils/portal";
import { exposeStrapiError, isModularContentType } from "utils";
import { CoreContext } from "context/CoreContext";
import { trackEvent } from "services/analytics";
import { buildSupportVideoRoute } from "utils/supportVideos";
import useI18n from "hooks/useI18n";

export default function VideoList({ initiallySharing = false, action, editing, emptyText, error, errorText, errorTitle, favorite, favoriteLoading, filter, icon, label, listOption, loading, nobutton, noShare, onToggleFavorite, remove, renderOption, retry, setRenderOption, student, portal, enablePdfPreview, data, wide }) {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`${to}`.startsWith('/') ? to : `/${to}`), [history]);
  const resolvedPortal = normalizePortal(portal || (student ? 'essentials' : null));
  const hasPortalShell = resolvedPortal !== 'universe';

  const DEFAULT_SHARE_DAYS = 7
  const [shareLink, setShareLink] = useState(initiallySharing)
  const [shareDays, setShareDays] = useState(DEFAULT_SHARE_DAYS)
  const { setModal, user } = useContext(CoreContext)
  const { t } = useI18n()

  const [selectedItems, setSelectedItems] = useState([])

  const toggleSelectedItem = (item) => {
    setSelectedItems((prev) => {
      const exists = prev?.some((p) => p?.targetUid === item?.targetUid && p?.targetDocumentId === item?.targetDocumentId)
      if (exists) {
        return prev?.filter((p) => !(p?.targetUid === item?.targetUid && p?.targetDocumentId === item?.targetDocumentId))
      }
      return [...prev, item]
    })
  }

  const isSelected = (uid, docId) => {
    return selectedItems?.some((p) => p?.targetUid === uid && p?.targetDocumentId === docId)
  }

  const createLinkTemporary = async () => {
    if (!selectedItems?.length) return

    const result = await CreateShareLink({ items: selectedItems, days: shareDays })
    if (!exposeStrapiError(result)) {
      setModal({ type: 'share', token: result?.token, days: result?.days || shareDays })
      setShareLink(false)
      setShareDays(DEFAULT_SHARE_DAYS)
      setSelectedItems([])
    }
  }

  const handleShare = () => {
    if (!shareLink) {
      setShareDays(DEFAULT_SHARE_DAYS)
      setShareLink(true)
      return
    }
    createLinkTemporary()
  }

  const cancel = () => {
    setShareLink(false)
    setShareDays(DEFAULT_SHARE_DAYS)
    setSelectedItems([])
  }

  const handleDownload = (file, currentFile) => {
    if (data?.supportVideos) {
      navigate(buildSupportVideoRoute({ categoryId: data.categoryId, contentId: file?.documentId || file?.id }))
      return
    }

    const normalizeExtension = (value = "") => `${value}`.toLowerCase().split("?")[0].split("#")[0].replace(/^\./, "")
    const normalizeUrl = (value = "") => `${value}`.toLowerCase().split("?")[0].split("#")[0]
    const extension = normalizeExtension(currentFile?.extension || currentFile?.url?.split(".")?.pop())
    const currentUrl = normalizeUrl(currentFile?.url)
    const mimeType = `${file?.mime || file?.file?.mime || file?.document?.mime || ""}`.toLowerCase()
    const isPdf = extension === "pdf" || currentUrl.endsWith(".pdf") || mimeType === "application/pdf"
    const isProtectedDownload = `${currentFile?.url || ''}`.includes('/s3-download?token=')
    const trackFileAction = (eventType) => trackEvent(eventType, {
      content_type: file?.type || file?.uid || 'file',
      content_id: file?.id || file?.documentId,
      resource_id: file?.file?.id || file?.document?.id || file?.documentId,
      content_name: file?.title,
      file_extension: extension,
    })

    if (isProtectedDownload) {
      const protectedUrl = buildS3DownloadUrl({
        sourceUrl: currentFile.url,
        filename: file?.title || file?.file || file?.document || 'arquivo',
        disposition: enablePdfPreview && isPdf ? 'inline' : 'attachment',
      })
      if (enablePdfPreview && isPdf) {
        trackFileAction('PDF_OPEN')
        setModal({ type: 'pdf-preview', title: file?.title, url: protectedUrl })
      } else {
        trackFileAction('FILE_DOWNLOAD')
        window.location.href = protectedUrl
      }
      return
    }

    if (enablePdfPreview && isPdf && currentFile?.url) {
      trackFileAction('PDF_OPEN')
      setModal({
        type: 'pdf-preview',
        title: file?.title,
        url: buildS3DownloadUrl({
          sourceUrl: currentFile.url,
          filename: file?.title || file?.file || file?.document || 'preview.pdf',
          disposition: 'inline',
        }),
      })
      return
    }

    if (!isModularContentType(file?.type)) {
      if (!currentFile?.url) return

      trackFileAction('FILE_DOWNLOAD')
      window.location.href = buildS3DownloadUrl({
        sourceUrl: currentFile?.url,
        filename: file?.title || file?.file || file?.document || 'arquivo',
      })
      return
    }
    if (hasPortalShell) {
      navigate(buildPortalDetailRoute(resolvedPortal, file?.type, file?.id))
      return
    }
    navigate(`mode/${file?.type}/${file?.id}${favorite ? `/${file?.lesson}` : ``}`)
  };

  const handlePreviewAction = useCallback((actionItem) => {
    if (!actionItem?.type || !actionItem?.value) return;

    if (actionItem.type === 'navigate') {
      navigate(actionItem.value)
    }
  }, [navigate])

  const buildPreviewCardItem = useCallback((item) => ({
    ...item,
    imageUrl: item?.image?.url || item?.cover,
    action: {
      type: 'navigate',
      value: hasPortalShell
        ? buildPortalDetailRoute(resolvedPortal, item?.type, item?.id)
        : `/mode/${item?.type}/${item?.id}${favorite ? `/${item?.lesson}` : ''}`,
    },
  }), [favorite, hasPortalShell, resolvedPortal])

  const buildItemKey = useCallback((item, prefix = 'item') => (
    `${prefix}-${item?.uid || item?.type || 'content'}-${item?.documentId || item?.id || item?.title || 'unknown'}`
  ), [])

  const isTrailDocumentItem = useCallback((item) => (
    data?.type === 'knowledge-trail-items' && item?.type === 'documents'
  ), [data?.type])

  const allSelectableItems = useMemo(() => {
    return (data?.data || []).map((item) => ({
      targetUid: item?.uid,
      targetDocumentId: item?.documentId,
    })).filter(i => i?.targetUid && i?.targetDocumentId);
  }, [data?.data]);

  const allSelected = useMemo(() => {
    if (!allSelectableItems.length) return false;
    return allSelectableItems.every(i =>
      selectedItems.some(s => s.targetUid === i.targetUid && s.targetDocumentId === i.targetDocumentId)
    );
  }, [allSelectableItems, selectedItems]);

  const selectAll = () => {
    setSelectedItems(allSelectableItems);
  };

  const unselectAll = () => {
    setSelectedItems([]);
  };

  const isStaff = useMemo(() => isStaffUser(user), [user]);

  return (
    <Wrapper white={data?.type === 'articles'} data={data} wide={wide} listOption={listOption} shareLink={shareLink} setShareLink={handleShare} cancel={cancel}
      shareDays={shareDays} setShareDays={setShareDays}
      label={label} icon={icon} noShare={noShare || !isStaff} action={action} nobutton={nobutton} filter={filter} renderOption={renderOption} setRenderOption={setRenderOption} allSelected={allSelected} selectAll={selectAll} unselectAll={unselectAll}
    >
      <VideoListContainer>
        {error ? (
          <CatalogState
            actionLabel={t('try_again')}
            description={errorText}
            onAction={retry}
            title={errorTitle || t('support_videos_unavailable')}
          />
        ) : !data?.data?.length && emptyText ? (
          <CatalogState title={emptyText} />
        ) : !data?.data?.length ? null : (
          <HorizontalScrollCarousel grid={data?.supportVideos} viewAll={wide} gap={data?.supportVideos ? 20 : 12}>
          {renderOption !== 'gallery' ? null :
            <>
              {data?.data?.map((video) => (
                !shareLink && isTrailDocumentItem(video) ? (
                  <TrailDocumentCard
                    key={buildItemKey(video, 'trail-document')}
                    item={video}
                    handleDownload={handleDownload}
                  />
                ) : !shareLink && isModularContentType(video?.type) ? (
                  <DashboardHomeCard
                    key={buildItemKey(video, 'preview')}
                    item={buildPreviewCardItem(video)}
                    onAction={handlePreviewAction}
                  />
                ) : (
                  <VideoCard
                    supportVideo={data?.supportVideos}
                    shareLink={shareLink}
                    selected={isSelected(video?.uid, video?.documentId)}
                    onToggleSelect={toggleSelectedItem}
                    noShare={noShare}
                    handleDownload={handleDownload}
                    key={buildItemKey(video, 'card')}
                    video={video}
                    wide={wide}
                    favorite={favorite ? video : null}
                    editing={editing}
                    remove={() => remove(video)}
                    loading={
                      loading === video?.id ||
                      loading === video?.favoriteId ||
                      favoriteLoading === video?.documentId
                    }
                    onToggleFavorite={onToggleFavorite}
                  />
                )
              ))}
            </>
          }
          {renderOption !== 'list' ? null :
            <>
              {data?.data?.map((m) => (
                <ItemsList
                  item={m}
                  key={buildItemKey(m, 'list')}
                  handleDownload={handleDownload}
                  shareLink={shareLink}
                  selected={isSelected(m?.uid, m?.documentId)}
                  onToggleSelect={toggleSelectedItem}
                  noShare={noShare}
                  onToggleFavorite={onToggleFavorite}
                  favoriteLoading={favoriteLoading === m?.documentId} />
              ))}
            </>
          }
          </HorizontalScrollCarousel>
        )}
      </VideoListContainer>
    </Wrapper>
  );
}
